import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { Dumbbell, MapPin, X, Plus, Trash2, Check, Minus, ChevronUp, ChevronDown, Calendar as CalendarIcon, Footprints } from "lucide-react";
import { PlatformMap } from "./PlatformMap";
import { Workout, Exercise, useWorkouts } from "@/hooks/useWorkouts";
import { supabase } from "@/integrations/supabase/client";
import { ExerciseProgressModal } from "./ExerciseProgressModal";
import { format, parse } from "date-fns";
import { cn } from "@/lib/utils";
import { Analytics } from "@/lib/analytics";

// Individual set structure for editing
interface EditWorkoutSet {
  id: string;
  reps: number | null;
  weight: number | null;
  completed: boolean;
}

// Exercise with individual sets for editing  
interface EditWorkoutExercise {
  name: string;
  sets: EditWorkoutSet[];
  notes?: string;
  muscle_groups: string[];
  equipment?: string;
  originalIds: string[]; // Track original exercise IDs from database
}

interface EditWorkoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  workout: Workout | null;
}

export const EditWorkoutModal = ({ isOpen, onClose, workout }: EditWorkoutModalProps) => {
  const { updateWorkout, deleteWorkout, addExerciseToWorkout, updateExercise, deleteExercise, refetch } = useWorkouts();
  
  // Workout details state
  const [title, setTitle] = useState("");
  const [workoutType, setWorkoutType] = useState("");
  const [difficulty, setDifficulty] = useState("medium");
  const [notes, setNotes] = useState("");
  const [durationMinutes, setDurationMinutes] = useState("");
  const [caloriesBurned, setCaloriesBurned] = useState("");
  const [startDate, setStartDate] = useState<Date>();
  const [startTime, setStartTime] = useState("");
  const [endDate, setEndDate] = useState<Date>();
  const [endTime, setEndTime] = useState("");
  
  // Exercises state - now using the new structure
  const [exercises, setExercises] = useState<EditWorkoutExercise[]>([]);
  const [newExercise, setNewExercise] = useState({
    name: "",
    sets: "",
    reps: "",
    weight_kg: "",
    distance_km: "",
    equipment: "",
    muscle_groups: [] as string[],
    notes: ""
  });
  
  // Running workout state
  const [totalDistance, setTotalDistance] = useState("");
  const [totalTime, setTotalTime] = useState("");
  
  const [saving, setSaving] = useState(false);
  
  // Progress modal state
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [selectedExerciseForProgress, setSelectedExerciseForProgress] = useState("");

  // Initialize form with workout data
  useEffect(() => {
    if (workout && isOpen) {
      setTitle(workout.title);
      setWorkoutType(workout.workout_type || "");
      setDifficulty(workout.difficulty_level || "medium");
      setNotes(workout.notes || "");
      setDurationMinutes(workout.duration_minutes?.toString() || "");
      setCaloriesBurned(workout.calories_burned?.toString() || "");
      
      // Initialize start date/time
      if (workout.started_at) {
        const startDateTime = new Date(workout.started_at);
        setStartDate(startDateTime);
        setStartTime(format(startDateTime, "HH:mm"));
      }
      
      // Initialize end date/time
      if (workout.completed_at) {
        const endDateTime = new Date(workout.completed_at);
        setEndDate(endDateTime);
        setEndTime(format(endDateTime, "HH:mm"));
      }
      
      // Convert exercises to the new structure - group by exercise name
      // IMPORTANT: Use set_details JSON array when available (new format)
      if (workout.exercises && workout.exercises.length > 0) {
        const groupedExercises = workout.exercises.reduce((acc, exercise) => {
          if (exercise.name === "Running") return acc; // Skip running exercises for strength workouts
          
          // Cast to access set_details which exists in DB but not in Exercise type
          const exerciseWithDetails = exercise as Exercise & { set_details?: any[] };
          
          if (!acc[exercise.name]) {
            acc[exercise.name] = {
              name: exercise.name,
              sets: [],
              notes: exercise.notes || "",
              muscle_groups: exercise.muscle_groups || [],
              equipment: exercise.equipment || "",
              originalIds: []
            };
          }
          
          // Check if set_details is available (new format) - it contains all sets as JSON array
          const setDetails = exerciseWithDetails.set_details;
          if (setDetails && Array.isArray(setDetails) && setDetails.length > 0) {
            // Parse set_details JSON array - this has all the sets
            setDetails.forEach((setDetail: any) => {
              acc[exercise.name].sets.push({
                id: crypto.randomUUID(),
                reps: setDetail.reps || null,
                weight: setDetail.weight_kg || null,
                completed: setDetail.completed !== false // Default to true for existing sets
              });
            });
          } else {
            // Fallback to legacy format (one row per set)
            acc[exercise.name].sets.push({
              id: exercise.id || crypto.randomUUID(),
              reps: exercise.reps,
              weight: exercise.weight_kg,
              completed: true // All existing sets are considered completed
            });
          }
          
          acc[exercise.name].originalIds.push(exercise.id || "");
          
          return acc;
        }, {} as Record<string, EditWorkoutExercise>);
        
        setExercises(Object.values(groupedExercises));
      } else {
        setExercises([]);
      }
      
      // For running workouts, extract distance and time from the running exercise
      if ((workout.workout_type === "running" || workout.workout_type === "Running") && workout.exercises?.length > 0) {
        const runningExercise = workout.exercises.find(e => e.name === "Running");
        if (runningExercise) {
          setTotalDistance(runningExercise.distance_km?.toString() || "");
          if (runningExercise.duration_seconds) {
            const hours = Math.floor(runningExercise.duration_seconds / 3600);
            const minutes = Math.floor((runningExercise.duration_seconds % 3600) / 60);
            const seconds = runningExercise.duration_seconds % 60;
            setTotalTime(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
          }
        }
      }
    }
  }, [workout, isOpen]);

  const resetForm = () => {
    setTitle("");
    setWorkoutType("");
    setDifficulty("medium");
    setNotes("");
    setDurationMinutes("");
    setCaloriesBurned("");
    setStartDate(undefined);
    setStartTime("");
    setEndDate(undefined);
    setEndTime("");
    setExercises([]);
    setTotalDistance("");
    setTotalTime("");
    setNewExercise({
      name: "",
      sets: "",
      reps: "",
      weight_kg: "",
      distance_km: "",
      equipment: "",
      muscle_groups: [],
      notes: ""
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const convertTimeToSeconds = (timeStr: string) => {
    const [hours, minutes, seconds] = timeStr.split(':').map(Number);
    return (hours || 0) * 3600 + (minutes || 0) * 60 + (seconds || 0);
  };

  const addExercise = () => {
    if (!newExercise.name.trim()) {
      toast.error("Please enter an exercise name");
      return;
    }

    const newWorkoutExercise: EditWorkoutExercise = {
      name: newExercise.name.trim(),
      sets: [{ 
        id: crypto.randomUUID(), 
        reps: newExercise.reps ? parseInt(newExercise.reps) : null, 
        weight: newExercise.weight_kg ? parseFloat(newExercise.weight_kg) : null, 
        completed: true 
      }],
      notes: newExercise.notes || "",
      muscle_groups: newExercise.muscle_groups,
      equipment: newExercise.equipment || "",
      originalIds: []
    };

    setExercises(prev => [...prev, newWorkoutExercise]);
    setNewExercise({
      name: "",
      sets: "",
      reps: "",
      weight_kg: "",
      distance_km: "",
      equipment: "",
      muscle_groups: [],
      notes: ""
    });
  };

  const removeExercise = (exerciseIndex: number) => {
    setExercises(prev => prev.filter((_, index) => index !== exerciseIndex));
  };

  const moveExercise = (exerciseIndex: number, direction: 'up' | 'down') => {
    const newExercises = [...exercises];
    const targetIndex = direction === 'up' ? exerciseIndex - 1 : exerciseIndex + 1;
    
    if (targetIndex < 0 || targetIndex >= newExercises.length) return;
    
    // Swap exercises
    [newExercises[exerciseIndex], newExercises[targetIndex]] = 
    [newExercises[targetIndex], newExercises[exerciseIndex]];
    
    setExercises(newExercises);
  };

  // Add set to an exercise
  const addSet = (exerciseIndex: number) => {
    setExercises(prev => prev.map((exercise, index) => 
      index === exerciseIndex 
        ? {
            ...exercise,
            sets: [...exercise.sets, {
              id: crypto.randomUUID(),
              reps: null,
              weight: null,
              completed: true
            }]
          }
        : exercise
    ));
  };

  // Remove set from an exercise
  const removeSet = (exerciseIndex: number, setIndex: number) => {
    setExercises(prev => prev.map((exercise, index) => 
      index === exerciseIndex && exercise.sets.length > 1
        ? {
            ...exercise,
            sets: exercise.sets.filter((_, sIndex) => sIndex !== setIndex)
          }
        : exercise
    ));
  };

  // Update set data
  const updateSet = (exerciseIndex: number, setIndex: number, field: 'reps' | 'weight', value: number | null) => {
    setExercises(prev => prev.map((exercise, index) => 
      index === exerciseIndex
        ? {
            ...exercise,
            sets: exercise.sets.map((set, sIndex) => 
              sIndex === setIndex
                ? { ...set, [field]: value }
                : set
            )
          }
        : exercise
    ));
  };

  // Toggle set completion
  const completeSet = (exerciseIndex: number, setIndex: number) => {
    setExercises(prev => prev.map((exercise, index) => 
      index === exerciseIndex
        ? {
            ...exercise,
            sets: exercise.sets.map((set, sIndex) => 
              sIndex === setIndex
                ? { ...set, completed: !set.completed }
                : set
            )
          }
        : exercise
    ));
  };

  const handleMuscleGroupToggle = (group: string) => {
    setNewExercise(prev => ({
      ...prev,
      muscle_groups: prev.muscle_groups.includes(group)
        ? prev.muscle_groups.filter(g => g !== group)
        : [...prev.muscle_groups, group]
    }));
  };

  const saveWorkout = async () => {
    if (!workout || !title.trim()) {
      toast.error("Please enter a workout title");
      return;
    }

    setSaving(true);
    try {
      // Build started_at timestamp if date and time are provided
      let startedAt = null;
      if (startDate && startTime) {
        const [hours, minutes] = startTime.split(':');
        const startDateTime = new Date(startDate);
        startDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        startedAt = startDateTime.toISOString();
      }

      // Build completed_at timestamp if date and time are provided
      let completedAt = null;
      if (endDate && endTime) {
        const [hours, minutes] = endTime.split(':');
        const endDateTime = new Date(endDate);
        endDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
        completedAt = endDateTime.toISOString();
      }

      // Update workout details
      const workoutData = {
        title: title.trim(),
        workout_type: workoutType,
        difficulty_level: difficulty,
        duration_minutes: durationMinutes ? parseInt(durationMinutes) : null,
        calories_burned: caloriesBurned ? parseInt(caloriesBurned) : null,
        notes: notes.trim() || null,
        started_at: startedAt,
        completed_at: completedAt
      };

      const { error: workoutError } = await updateWorkout(workout.id!, workoutData, { skipRefresh: true });
      
      if (workoutError) {
        throw new Error('Failed to update workout');
      }

      // Update exercises for strength workouts
      if (workoutType !== "running" && workoutType !== "Running") {
        // First, remove all existing exercises for this workout (except Running)
        const { error: deleteAllError } = await supabase
          .from("exercises")
          .delete()
          .eq("workout_id", workout.id!)
          .neq("name", "Running");
        if (deleteAllError) throw deleteAllError;


        // Now add all exercises with set_details (single row per exercise with all sets in JSON)
        for (const exercise of exercises) {
          const completedSets = exercise.sets.filter(set => set.completed);
          if (completedSets.length === 0) continue;

          // Build set_details JSON array with all sets
          const setDetails = completedSets.map((set, idx) => ({
            set_number: idx + 1,
            reps: set.reps,
            weight_kg: set.weight,
            completed: set.completed
          }));

          // Use the first completed set for the main reps/weight columns (for backwards compat)
          const firstSet = completedSets[0];

          const { error } = await addExerciseToWorkout(
            workout.id!,
            {
              name: exercise.name,
              sets: completedSets.length, // Total number of sets
              reps: firstSet.reps,
              weight_kg: firstSet.weight,
              equipment: exercise.equipment || null,
              muscle_groups: exercise.muscle_groups,
              notes: exercise.notes || null,
              duration_seconds: null,
              rest_seconds: null,
              distance_km: null,
              set_details: setDetails // All sets stored in JSON array
            },
            { skipRefresh: true }
          );
          if (error) throw error;
        }
      }

      // Handle running workout special case
      if (workoutType === "running" || workoutType === "Running") {
        const runningExercise = {
          name: 'Running',
          distance_km: totalDistance ? parseFloat(totalDistance) : null,
          duration_seconds: totalTime ? convertTimeToSeconds(totalTime) : null,
          notes: null,
          muscle_groups: ['legs', 'cardiovascular'],
          equipment: 'none',
          sets: null,
          reps: null,
          weight_kg: null,
          rest_seconds: null
        };

        // Find existing running exercise or create new one
        const existingRunningExercise = workout.exercises?.find(e => e.name === "Running");
        if (existingRunningExercise) {
          const { error } = await updateExercise(existingRunningExercise.id!, runningExercise, { skipRefresh: true });
          if (error) throw error;
        } else {
          const { error } = await addExerciseToWorkout(workout.id!, runningExercise, { skipRefresh: true });
          if (error) throw error;
        }
      }

      await refetch();
      Analytics.workoutEdited(workout.id!, title.trim());
      toast.success("Workout updated successfully!");
      handleClose();
    } catch (error) {
      console.error('Error updating workout:', error);
      toast.error("Failed to update workout. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteWorkout = async () => {
    if (!workout) return;
    
    try {
      const { error } = await deleteWorkout(workout.id!);
      if (error) {
        throw new Error('Failed to delete workout');
      }
      
      Analytics.workoutDeleted(workout.id!, workout.title || 'Untitled Workout');
      toast.success("Workout deleted successfully!");
      handleClose();
    } catch (error) {
      console.error('Error deleting workout:', error);
      toast.error("Failed to delete workout. Please try again.");
    }
  };

  if (!workout) return null;

  const muscleGroups = ['chest', 'back', 'shoulders', 'biceps', 'triceps', 'legs', 'glutes', 'core', 'cardio'];

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl lg:max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base sm:text-lg">
            {workoutType === 'running' ? (
              <MapPin className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            ) : (
              <Dumbbell className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            )}
            Edit Workout
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 sm:space-y-6">
          {/* Basic Workout Details */}
          <Card>
            <CardHeader className="p-4 sm:p-6">
              <CardTitle className="text-base sm:text-lg font-normal">Workout Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-title">Title</Label>
                  <Input
                    id="edit-title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className="bg-background/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-type">Type</Label>
                  <Select value={workoutType} onValueChange={setWorkoutType}>
                    <SelectTrigger className="bg-background/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="strength">Strength</SelectItem>
                      <SelectItem value="cardio">Cardio</SelectItem>
                      <SelectItem value="running">Running</SelectItem>
                      <SelectItem value="yoga">Yoga</SelectItem>
                      <SelectItem value="mixed">Mixed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-difficulty" className="text-sm">Difficulty</Label>
                  <Select value={difficulty} onValueChange={setDifficulty}>
                    <SelectTrigger className="bg-background/50 h-9 sm:h-10">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="easy">Easy</SelectItem>
                      <SelectItem value="medium">Medium</SelectItem>
                      <SelectItem value="hard">Hard</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-duration" className="text-sm">Duration (min)</Label>
                  <Input
                    id="edit-duration"
                    type="number"
                    value={durationMinutes}
                    onChange={(e) => setDurationMinutes(e.target.value)}
                    className="bg-background/50 h-9 sm:h-10"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-calories" className="text-sm">Calories</Label>
                  <Input
                    id="edit-calories"
                    type="number"
                    value={caloriesBurned}
                    onChange={(e) => setCaloriesBurned(e.target.value)}
                    className="bg-background/50 h-9 sm:h-10"
                  />
                </div>
              </div>

              {/* Start and End Time for Completed Workouts */}
              {workout.completed_at && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-sm">Start Time</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "justify-start text-left font-normal bg-background/50 flex-1",
                              !startDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {startDate ? format(startDate, "MMM d, yyyy") : "Pick date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={startDate}
                            onSelect={setStartDate}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <Input
                        type="time"
                        value={startTime}
                        onChange={(e) => setStartTime(e.target.value)}
                        className="bg-background/50 w-32"
                      />
                    </div>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-sm">End Time</Label>
                    <div className="flex gap-2">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "justify-start text-left font-normal bg-background/50 flex-1",
                              !endDate && "text-muted-foreground"
                            )}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {endDate ? format(endDate, "MMM d, yyyy") : "Pick date"}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={endDate}
                            onSelect={setEndDate}
                            initialFocus
                            className="pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      <Input
                        type="time"
                        value={endTime}
                        onChange={(e) => setEndTime(e.target.value)}
                        className="bg-background/50 w-32"
                      />
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Running Workout Rich Summary */}
          {(workoutType === "running" || workoutType === "Running") && (() => {
            const runningExercise = workout.exercises?.find(e => e.name === 'Running');
            let runData: any = null;
            if (runningExercise?.notes) {
              try { runData = JSON.parse(runningExercise.notes); } catch {}
            }
            const distKm = runningExercise?.distance_km;
            const durSec = runningExercise?.duration_seconds;
            const pace = runData?.pace;
            const splits: any[] = runData?.splits || [];
            const routePoints: { lat: number; lng: number }[] = runData?.routePoints || [];
            const routeLine: [number, number][] = routePoints.map(p => [p.lat, p.lng]);
            const feeling = runData?.feeling;
            const avgSpeed = runData?.avgSpeedKmh;
            const pauseCount = runData?.pauseCount;

            const formatRunTime = (sec: number) => {
              const h = Math.floor(sec / 3600);
              const m = Math.floor((sec % 3600) / 60);
              const s = sec % 60;
              return h > 0 
                ? `${h}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`
                : `${m}:${s.toString().padStart(2, '0')}`;
            };

            return (
              <Card>
                <CardHeader className="p-4 sm:p-6">
                  <CardTitle className="text-base sm:text-lg font-normal flex items-center gap-2">
                    <Footprints className="h-4 w-4 text-primary" />
                    Running Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
                  {/* Route Map */}
                  {routeLine.length > 1 && (
                    <div className="rounded-xl overflow-hidden border border-border/30 h-48">
                      <PlatformMap
                        center={routeLine[Math.floor(routeLine.length / 2)]}
                        routePoints={routeLine}
                        showUserMarker={false}
                        showRoute={true}
                        showStartFinishMarkers={true}
                        showKmMarkers={true}
                        fitToRoute={true}
                        autoFollow={false}
                        className="w-full h-full"
                        darkMode={true}
                        interactive={true}
                        zoom={13}
                      />
                    </div>
                  )}

                  {/* Stats Grid */}
                  <div className="grid grid-cols-2 gap-3 text-sm bg-muted/30 rounded-xl p-4 border border-border/30">
                    <div className="text-center">
                      <div className="font-bold text-lg text-primary">{distKm ?? '—'} km</div>
                      <div className="text-xs text-muted-foreground">Distance</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg">{durSec ? formatRunTime(durSec) : (durationMinutes ? `${durationMinutes}min` : '—')}</div>
                      <div className="text-xs text-muted-foreground">Total Time</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg">{pace || '—'}</div>
                      <div className="text-xs text-muted-foreground">Avg Pace /km</div>
                    </div>
                    <div className="text-center">
                      <div className="font-bold text-lg">{caloriesBurned || '—'}</div>
                      <div className="text-xs text-muted-foreground">Calories</div>
                    </div>
                  </div>

                  {/* Additional Info */}
                  {(avgSpeed || feeling || pauseCount !== undefined) && (
                    <div className="flex flex-wrap gap-2">
                      {avgSpeed && (
                        <Badge variant="outline" className="text-xs">{avgSpeed} km/h avg</Badge>
                      )}
                      {feeling && (
                        <Badge variant="outline" className="text-xs">Feeling: {feeling}</Badge>
                      )}
                      {pauseCount !== undefined && pauseCount > 0 && (
                        <Badge variant="outline" className="text-xs">{pauseCount} pause{pauseCount > 1 ? 's' : ''}</Badge>
                      )}
                    </div>
                  )}

                  {/* Km Splits */}
                  {splits.length > 0 && (
                    <div>
                      <label className="text-xs text-muted-foreground mb-2 block font-medium">Kilometer Splits</label>
                      <div className="space-y-1 max-h-40 overflow-y-auto bg-muted/20 rounded-lg p-2">
                        {splits.map((split: any, idx: number) => (
                          <div key={idx} className="flex justify-between items-center text-sm px-2 py-1.5 rounded-md hover:bg-muted/20">
                            <span className="text-muted-foreground font-medium">
                              {split.km} km
                            </span>
                            <div className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground">{split.time}</span>
                              <span className="font-mono font-medium text-primary">{split.pace}/km</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Distance and time are read-only for outdoor runs (GPS-tracked) */}
                  {routeLine.length === 0 && (
                    <>
                      <Separator />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="edit-distance" className="text-xs">Edit Distance (km)</Label>
                          <Input
                            id="edit-distance"
                            type="number"
                            step="0.1"
                            value={totalDistance}
                            onChange={(e) => setTotalDistance(e.target.value)}
                            className="bg-background/50 h-9"
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="edit-time" className="text-xs">Edit Time (HH:MM:SS)</Label>
                          <Input
                            id="edit-time"
                            value={totalTime}
                            onChange={(e) => setTotalTime(e.target.value)}
                            className="bg-background/50 h-9"
                          />
                        </div>
                      </div>
                    </>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          {/* Gym Workout Exercises */}
          {workoutType !== "running" && workoutType !== "Running" && (
            <Card>
              <CardHeader className="p-4 sm:p-6">
                <CardTitle className="text-base sm:text-lg font-normal">Exercises</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 p-4 sm:p-6 pt-0">
                {/* Existing Exercises */}
                {exercises.map((exercise, exerciseIndex) => (
                  <div key={`${exercise.name}-${exerciseIndex}`} className="p-3 sm:p-4 bg-background/20 rounded-lg border border-border/30">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0 mb-3">
                      <h4 className="font-medium text-sm sm:text-base">{exercise.name}</h4>
                      <div className="flex items-center gap-1 sm:gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedExerciseForProgress(exercise.name);
                            setShowProgressModal(true);
                          }}
                          className="text-xs h-8"
                        >
                          Progress
                        </Button>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveExercise(exerciseIndex, 'up')}
                            disabled={exerciseIndex === 0}
                            className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                          >
                            <ChevronUp className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => moveExercise(exerciseIndex, 'down')}
                            disabled={exerciseIndex === exercises.length - 1}
                            className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                          >
                            <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" />
                          </Button>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeExercise(exerciseIndex)}
                          className="h-7 w-7 sm:h-8 sm:w-8 p-0"
                        >
                          <X className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Individual Sets */}
                    <div className="space-y-2 sm:space-y-3">
                      {exercise.sets.map((set, setIndex) => (
                        <div key={set.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-background/30 rounded-lg">
                          <div className="flex items-center justify-between sm:justify-start gap-2 sm:min-w-[80px]">
                            <span className="text-xs sm:text-sm font-medium">Set {setIndex + 1}:</span>
                            <Button
                              variant={set.completed ? "default" : "outline"}
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => completeSet(exerciseIndex, setIndex)}
                            >
                              <Check className="h-3 w-3" />
                            </Button>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-1 flex-wrap">
                            <div className="flex items-center gap-1">
                              <Input
                                type="number"
                                value={set.reps || ""}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => updateSet(exerciseIndex, setIndex, 'reps', e.target.value ? parseInt(e.target.value) : null)}
                                className="bg-background/50 h-8 w-14 sm:w-16 text-xs sm:text-sm text-center"
                                placeholder="0"
                              />
                              <span className="text-xs text-muted-foreground whitespace-nowrap">reps</span>
                            </div>
                            
                            <span className="text-muted-foreground">×</span>
                            
                            <div className="flex items-center gap-1">
                              <Input
                                type="text"
                                inputMode="decimal"
                                value={set.weight || ""}
                                onFocus={(e) => e.target.select()}
                                onChange={(e) => {
                                  const value = e.target.value.replace(',', '.');
                                  if (value === '' || /^\d*\.?\d*$/.test(value)) {
                                    const parsed = value ? Number(value) : null;
                                    updateSet(
                                      exerciseIndex,
                                      setIndex,
                                      'weight',
                                      parsed !== null && Number.isFinite(parsed) ? parsed : null
                                    );
                                  }
                                }}
                                className="bg-background/50 h-8 w-20 text-sm text-center"
                                placeholder="0"
                              />
                              <span className="text-xs text-muted-foreground">kg</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-6 w-6 p-0"
                              onClick={() => addSet(exerciseIndex)}
                            >
                              <Plus className="h-3 w-3" />
                            </Button>
                            {exercise.sets.length > 1 && (
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-6 w-6 p-0"
                                onClick={() => removeSet(exerciseIndex, setIndex)}
                              >
                                <Minus className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Exercise Notes */}
                    <div className="mt-3">
                      <Label className="text-xs">Notes</Label>
                      <Textarea
                        value={exercise.notes || ""}
                        onChange={(e) => {
                          setExercises(prev => prev.map((ex, index) => 
                            index === exerciseIndex ? { ...ex, notes: e.target.value } : ex
                          ));
                        }}
                        className="bg-background/50 h-16 text-sm mt-1"
                        placeholder="Exercise notes..."
                      />
                    </div>
                  </div>
                ))}

                <Separator />

                {/* Add New Exercise */}
                <div className="space-y-4 p-4 border border-border/50 rounded-lg">
                  <h5 className="font-medium">Add Exercise</h5>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      placeholder="Exercise name"
                      value={newExercise.name}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-background/50"
                    />
                    <Input
                      placeholder="Equipment"
                      value={newExercise.equipment}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, equipment: e.target.value }))}
                      className="bg-background/50"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <Input
                      type="number"
                      placeholder="Sets"
                      value={newExercise.sets}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, sets: e.target.value }))}
                      className="bg-background/50"
                    />
                    <Input
                      type="number"
                      placeholder="Reps"
                      value={newExercise.reps}
                      onChange={(e) => setNewExercise(prev => ({ ...prev, reps: e.target.value }))}
                      className="bg-background/50"
                    />
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="Weight (kg)"
                      value={newExercise.weight_kg}
                      onChange={(e) => {
                        const value = e.target.value.replace(',', '.');
                        if (value === '' || /^\d*\.?\d*$/.test(value)) {
                          setNewExercise(prev => ({ ...prev, weight_kg: value }));
                        }
                      }}
                      className="bg-background/50"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Muscle Groups</Label>
                    <div className="flex flex-wrap gap-2">
                      {muscleGroups.map((group) => (
                        <Badge
                          key={group}
                          variant={newExercise.muscle_groups.includes(group) ? "default" : "outline"}
                          className="cursor-pointer"
                          onClick={() => handleMuscleGroupToggle(group)}
                        >
                          {group}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <Button onClick={addExercise} className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Exercise
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Notes */}
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="space-y-2">
                <Label htmlFor="edit-notes" className="text-sm">Notes</Label>
                <Textarea
                  id="edit-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="bg-background/50 min-h-[80px] sm:min-h-[100px] text-sm"
                />
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 pt-2 sm:pt-4">
            <Button
              onClick={saveWorkout}
              disabled={saving || !title.trim()}
              className="flex-1 bg-gradient-primary text-black h-10 text-sm"
            >
              {saving ? "Saving..." : "Save Changes"}
            </Button>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="destructive" size="default" className="h-10 text-sm">
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Delete Workout</AlertDialogTitle>
                  <AlertDialogDescription>
                    Are you sure you want to delete this workout? This action cannot be undone and will permanently remove all exercise data from this workout.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={handleDeleteWorkout} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                    Delete Workout
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
            <Button variant="outline" onClick={handleClose} className="h-10 text-sm">
              Cancel
            </Button>
          </div>
        </div>

        {/* Exercise Progress Modal */}
        <ExerciseProgressModal
          isOpen={showProgressModal}
          onClose={() => setShowProgressModal(false)}
          exerciseName={selectedExerciseForProgress}
        />
      </DialogContent>
    </Dialog>
  );
};
